import { doc, collection, setDoc, deleteDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { db, functions } from '@/firebase'
import { httpsCallable } from 'firebase/functions'
import { v4 as uuidv4 } from 'uuid'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'
import { SMOKE_ISSUE_REPORT_ROUTE } from '../taggedCollections.js'

// Backups (district_admin only), issue reports (any valid role may file; only the
// product-owner email list may read; delete is false), pdfMetadata (any valid
// role create, admin delete), pdfAccessLogs (server writes only + immutable).
// issueReports has a closed field list — no __smokeTest. Cleanup finds them
// by route === SMOKE_ISSUE_REPORT_ROUTE.

export default {
  id: 'adminFerpa',
  label: 'Backups, issue reports, PDF logs',
  description:
    'backups are district_admin-only; issue reports are create-only for staff; PDF metadata follows school scope; direct PDF audit writes are denied.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const schoolTier = Array.isArray(ctx.tokenSchoolIds)
    if (schoolTier && !ctx.schoolId) {
      step.block(
        'admin / FERPA writes',
        'Select a school before probing school-scoped PDF metadata and access logs'
      )
      return
    }

    const backupRef = doc(db, 'backups', `smoke_bak_${ctx.runId.slice(-8)}`)
    const issueRef = doc(collection(db, 'issueReports'))
    const metaRef = doc(db, 'pdfMetadata', `smoke_pdf_${ctx.runId.slice(-8)}.pdf`)
    const logRef = doc(collection(db, 'pdfAccessLogs'))

    // pdfMetadata creates require inScope(request.resource.data); direct
    // pdfAccessLogs writes are always denied. Backups are district-only.
    const scopeOk = RULE_EXPECTATIONS.inScopeCreate(role, {
      tokenSchoolIds: ctx.tokenSchoolIds,
      schoolId: ctx.schoolId
    })
    const expectBackup = RULE_EXPECTATIONS.backupWrite(role)
    const expectIssueCreate = RULE_EXPECTATIONS.issueReportCreate()
    const expectIssueRead = RULE_EXPECTATIONS.issueReportRead(role, { email: ctx.user.email })
    const expectIssueDelete = RULE_EXPECTATIONS.issueReportDelete()
    const expectMetaWrite = RULE_EXPECTATIONS.pdfMetadataWrite() && scopeOk
    const expectMetaDelete = RULE_EXPECTATIONS.pdfMetadataDelete(role)
    const expectLogCreate = RULE_EXPECTATIONS.pdfAccessLogCreate() && scopeOk
    const expectLogRead = RULE_EXPECTATIONS.pdfAccessLogRead(role)
    const expectLogDelete = RULE_EXPECTATIONS.pdfAccessLogDelete()

    try {
      const recordActivity = httpsCallable(functions, 'recordAuditEvent')
      const auditProbe = {
        eventId: uuidv4(),
        type: 'system_access',
        action: 'audit_delivery_probe',
        schoolId: ctx.schoolId || null,
        details: { synthetic: true, smokeRunId: ctx.runId }
      }
      let recorded
      await step('audit collector binds the authenticated actor and records an event', async () => {
        const { data } = await recordActivity(auditProbe)
        if (data?.recorded !== true) throw new Error('Collector did not confirm the audit record')
        recorded = data
        return 'Server acknowledged the synthetic activity event'
      })
      await step('audit retry does not create a duplicate', async () => {
        const { data } = await recordActivity(auditProbe)
        if (!recorded || data?.duplicate !== true || data.recordId !== recorded.recordId) {
          throw new Error('Retry did not return the original audit record')
        }
        return 'Repeated event ID returned the original record'
      })
      await step('audit collector rejects a forged actor', async () => {
        try {
          await recordActivity({ ...auditProbe, eventId: uuidv4(), userId: 'forged-user' })
        } catch (error) {
          if (error?.code === 'functions/invalid-argument') return 'Forged identity rejected'
          throw error
        }
        throw new Error('Collector accepted a forged identity')
      })
      await step('direct audit SDK writes are denied', async () => {
        const { allowed } = await attempt(() =>
          setDoc(doc(collection(db, 'auditLogs')), {
            type: 'access_change',
            source: 'server_verified',
            userId: ctx.user.uid,
            timestamp: serverTimestamp(),
            ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
          })
        )
        return accessDetail(false, allowed)
      })
      if (
        recorded &&
        RULE_EXPECTATIONS.auditLogRead(role, {
          schoolId: ctx.schoolId,
          tokenSchoolIds: ctx.tokenSchoolIds
        })
      ) {
        await step(
          'saved audit readback has the real actor and client evidence label',
          async () => {
            const saved = await getDoc(doc(db, 'auditLogs', recorded.recordId))
            if (
              !saved.exists() ||
              saved.data().userId !== ctx.user.uid ||
              saved.data().source !== 'client_reported' ||
              !saved.data().timestamp
            ) {
              throw new Error('Audit readback did not match the authenticated actor')
            }
            return 'Server record verified; the synthetic audit entry is retained'
          }
        )
      }
      let backupCreated = false
      await step(`write backup probe (expected: ${fmtExpected(expectBackup)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(backupRef, {
            label: 'SMOKE',
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            createdAt: serverTimestamp()
          })
        )
        backupCreated = allowed
        return allowed
          ? `${accessDetail(expectBackup, allowed)} — backups/${backupRef.id}`
          : accessDetail(expectBackup, allowed)
      })
      if (backupCreated) {
        await step(`read backup probe (expected: ${fmtExpected(expectBackup)})`, async () => {
          const { allowed } = await attempt(() => getDoc(backupRef))
          return accessDetail(expectBackup, allowed)
        })
        await step(`delete backup probe (expected: ${fmtExpected(expectBackup)})`, async () => {
          const { allowed } = await attempt(() => deleteDoc(backupRef))
          if (allowed) backupCreated = false
          return accessDetail(expectBackup, allowed)
        })
      } else {
        step.skip('read/delete backup probe', 'Write denied — later steps skipped')
      }

      let issueCreated = false
      await step(`create issue report (expected: ${fmtExpected(expectIssueCreate)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(issueRef, {
            type: 'suggestion',
            message: 'SMOKE probe — safe to delete',
            route: SMOKE_ISSUE_REPORT_ROUTE,
            userId: ctx.user.uid,
            userEmail: ctx.user.email || '',
            userName: ctx.user.displayName || ctx.user.email || ctx.user.uid,
            userRole: role,
            schoolId: ctx.schoolId || 'smoke',
            host: 'smoke-test',
            userAgent: 'smoke-runner',
            status: 'new',
            createdAt: serverTimestamp()
          })
        )
        issueCreated = allowed
        return allowed
          ? `${accessDetail(expectIssueCreate, allowed)} — issueReports/${issueRef.id}`
          : accessDetail(expectIssueCreate, allowed)
      })

      await step(`read issue report (expected: ${fmtExpected(expectIssueRead)})`, async () => {
        const { allowed } = await attempt(() => getDoc(issueRef))
        return accessDetail(expectIssueRead, allowed)
      })

      if (issueCreated) {
        await step(
          `delete issue report (expected: ${fmtExpected(expectIssueDelete)})`,
          async () => {
            const { allowed } = await attempt(() => deleteDoc(issueRef))
            if (allowed) issueCreated = false
            return accessDetail(expectIssueDelete, allowed)
          }
        )
      } else {
        step.skip('delete issue report', 'Create denied — nothing to delete')
      }

      let metaCreated = false
      await step(`write PDF metadata (expected: ${fmtExpected(expectMetaWrite)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(metaRef, {
            originalName: 'smoke.pdf',
            studentId: 'smoke',
            uploadedBy: ctx.user.uid,
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            createdAt: serverTimestamp(),
            ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
          })
        )
        metaCreated = allowed
        return allowed
          ? `${accessDetail(expectMetaWrite, allowed)} — pdfMetadata/${metaRef.id}`
          : accessDetail(expectMetaWrite, allowed)
      })
      if (metaCreated) {
        await step(`delete PDF metadata (expected: ${fmtExpected(expectMetaDelete)})`, async () => {
          const { allowed } = await attempt(() => deleteDoc(metaRef))
          if (allowed) metaCreated = false
          return accessDetail(expectMetaDelete, allowed)
        })
      } else {
        step.skip('delete PDF metadata', 'Write denied — nothing to delete')
      }

      let logCreated = false
      await step(`create PDF access log (expected: ${fmtExpected(expectLogCreate)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(logRef, {
            fileId: metaRef.id,
            userId: ctx.user.uid,
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            createdAt: serverTimestamp(),
            ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
          })
        )
        logCreated = allowed
        return allowed
          ? `${accessDetail(expectLogCreate, allowed)} — pdfAccessLogs/${logRef.id}`
          : accessDetail(expectLogCreate, allowed)
      })

      if (logCreated) {
        await step(`read PDF access log (expected: ${fmtExpected(expectLogRead)})`, async () => {
          const { allowed } = await attempt(() => getDoc(logRef))
          return accessDetail(expectLogRead, allowed)
        })
        await step(
          `delete PDF access log (expected: ${fmtExpected(expectLogDelete)})`,
          async () => {
            const { allowed } = await attempt(() => deleteDoc(logRef))
            if (allowed) logCreated = false
            return accessDetail(expectLogDelete, allowed)
          }
        )
      } else {
        step.skip('read/delete PDF access log', 'Create denied — later steps skipped')
      }
    } finally {
      await deleteDoc(backupRef).catch(() => {})
      await deleteDoc(metaRef).catch(() => {})
    }
  }
}
